import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@openzeppelin/ui-components";
import { Icon } from "@ascend/shared/components";
import {
  PaginationControls,
  SearchBar,
  FilterTabs,
  ViewModeToggle,
  usePagination,
  PAGE_SIZE_OPTIONS,
} from "../components/ListControls";
import { CLIENT_USER_ACCESS_IDS, CLIENT_FACILITIES } from "../data/mockAdapter";

// ─── Types ──────────────────────────────────────────────────────────────────────

type FacilityStatus = "active" | "paused";
type StatusFilter = "all" | FacilityStatus;
type Eligibility = "eligible" | "action-required" | "not-eligible";

interface Facility {
  id: string;
  name: string;
  settlementTokens: string[];
  collateralTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  status: FacilityStatus;
  eligibility: Eligibility;
  eligibilityGap?: string; // shown for action-required / not-eligible states
}

const USER_ACCESS_IDS = CLIENT_USER_ACCESS_IDS;

// Per spec §7: eligibility = does the user's OnchainID carry the claims this
// CF requires? Mock derivation: anything in USER_ACCESS_IDS is fully eligible;
// the rest alternate between action-required (recoverable: complete a missing
// claim) and not-eligible (jurisdictional / hard block).
const ELIGIBILITY_GAPS: Record<string, { eligibility: Eligibility; gap?: string }> = {
  // example overrides keyed by facility id can go here
};

function deriveEligibility(facilityId: string, idx: number): { eligibility: Eligibility; gap?: string } {
  if (USER_ACCESS_IDS.has(facilityId)) return { eligibility: "eligible" };
  if (ELIGIBILITY_GAPS[facilityId]) return ELIGIBILITY_GAPS[facilityId];
  // Stable, deterministic mix: alternate by index so the list shows both states.
  return idx % 2 === 0
    ? { eligibility: "action-required", gap: "Complete KYC via Sumsub" }
    : { eligibility: "not-eligible", gap: "Jurisdiction (US) not supported" };
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility, idx) => {
  const { eligibility, gap } = deriveEligibility(facility.id, idx);
  return {
    id: facility.id,
    name: facility.name,
    settlementTokens: facility.settlementTokens,
    collateralTokens: facility.collateralTokens,
    interestRate: facility.interestRate,
    maxLTV: facility.maxLTV,
    liquidationThreshold: facility.liquidationThreshold,
    utilization: facility.utilization,
    status: facility.status,
    eligibility,
    eligibilityGap: gap,
  };
});

const ELIGIBILITY_CONFIG: Record<Eligibility, { label: string; className: string }> = {
  eligible: { label: "Eligible", className: "bg-green-100 text-green-700" },
  "action-required": { label: "Action required", className: "bg-amber-100 text-amber-700" },
  "not-eligible": { label: "Not eligible", className: "bg-red-50 text-red-700" },
};

function EligibilityBadge({ facility, compact }: { facility: Facility; compact?: boolean }) {
  const cfg = ELIGIBILITY_CONFIG[facility.eligibility];
  const showGap = !compact && facility.eligibility !== "eligible" && facility.eligibilityGap;
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.className}`}
      title={facility.eligibilityGap}
    >
      {showGap ? `${cfg.label}: ${facility.eligibilityGap}` : cfg.label}
    </span>
  );
}

const STATUS_CONFIG: Record<FacilityStatus, { label: string; className: string }> =
  {
    active: { label: "Active", className: "bg-green-100 text-green-700" },
    paused: { label: "Paused", className: "bg-yellow-100 text-yellow-700" },
  };

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CreditFacilitiesPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const { page, setPage, pageSize, setPageSize, paginate } = usePagination(
    PAGE_SIZE_OPTIONS[0],
  );

  const handleStatusChange = (v: StatusFilter) => {
    setStatusFilter(v);
    setPage(1);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let result = FACILITIES;

    if (statusFilter !== "all") {
      result = result.filter((v) => v.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.collateralTokens.some((t) => t.toLowerCase().includes(q)) ||
          v.settlementTokens.some((t) => t.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [statusFilter, search]);

  const statusCounts: Record<StatusFilter, number> = {
    all: FACILITIES.length,
    active: FACILITIES.filter((v) => v.status === "active").length,
    paused: FACILITIES.filter((v) => v.status === "paused").length,
  };

  const { paginated, totalItems } = paginate(filtered);

  const activeFacilities = FACILITIES.filter((v) => v.status === "active");
  const avgRate = activeFacilities.length
    ? (
        activeFacilities.reduce((s, v) => s + parseFloat(v.interestRate), 0) /
        activeFacilities.length
      ).toFixed(1)
    : "0.0";
  const avgUtil = activeFacilities.length
    ? (
        activeFacilities.reduce((s, v) => s + parseFloat(v.utilization), 0) /
        activeFacilities.length
      ).toFixed(1)
    : "0.0";

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name="streamline:bank" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              Discover Credit Facilities
            </h1>
            <p className="text-sm text-muted-foreground">
              Browse credit facilities to find the right terms for your margin
              accounts. Each facility defines which collateral and settlement
              tokens are accepted.
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Total Facilities"
          value={String(FACILITIES.length)}
          subtitle={`${activeFacilities.length} active`}
          icon={<Icon name="streamline:bank" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Your Access"
          value={String(USER_ACCESS_IDS.size)}
          subtitle="Facilities with access"
          icon={<Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Avg Interest Rate"
          value={`${avgRate}%`}
          subtitle="Active facilities"
          icon={<Icon name="streamline:discount-percent-circle" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Avg Utilization"
          value={`${avgUtil}%`}
          subtitle="Pool utilization rate"
          icon={<Icon name="streamline:money-graph-bar-product-data-bars-analysis-analytics-graph-business-chart" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Facility grid/list */}
      <Card>
        <CardHeader className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Your Credit Facilities</CardTitle>
              <CardDescription>
                Click a facility card to view details and manage your position.
              </CardDescription>
            </div>
            <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
            <FilterTabs
              filters={STATUS_FILTERS}
              active={statusFilter}
              onChange={handleStatusChange}
              counts={statusCounts}
            />
            <SearchBar
              value={search}
              onChange={handleSearch}
              placeholder="Search by name or token…"
              className="w-full sm:w-64"
            />
          </div>

          {paginated.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-sm text-muted-foreground">
                No facilities found.
              </p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {paginated.map((facility) => (
                <FacilityCard
                  key={facility.id}
                  facility={facility}
                  onClick={() => navigate(`/credit-facilities/${facility.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {paginated.map((facility) => (
                <FacilityRow
                  key={facility.id}
                  facility={facility}
                  onClick={() => navigate(`/credit-facilities/${facility.id}`)}
                />
              ))}
            </div>
          )}

          <PaginationControls
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={totalItems}
            label="facilities"
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FacilityCard({
  facility,
  onClick,
}: {
  facility: Facility;
  onClick: () => void;
}) {
  const status = STATUS_CONFIG[facility.status];
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-xl border p-5 sm:p-6 hover:bg-accent/50 transition-colors ${
        facility.status === "paused" ? "border-yellow-300" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{facility.name}</h3>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}
            >
              {status.label}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Collateral: {facility.collateralTokens.join(", ")}
          </p>
        </div>
        <EligibilityBadge facility={facility} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-y-3 gap-x-4">
        <div>
          <div className="text-xs text-muted-foreground">Interest Rate</div>
          <div className="text-lg font-bold">{facility.interestRate}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Max LTV</div>
          <div className="text-lg font-bold">{facility.maxLTV}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Settlement Tokens</div>
          <div className="text-lg font-bold">
            {facility.settlementTokens.join(", ")}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Utilization</div>
          <div className="text-lg font-bold">{facility.utilization}</div>
        </div>
      </div>
    </div>
  );
}

function FacilityRow({
  facility,
  onClick,
}: {
  facility: Facility;
  onClick: () => void;
}) {
  const status = STATUS_CONFIG[facility.status];
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between hover:bg-accent/30 transition-colors px-2 rounded-lg first:pt-2 last:pb-2 ${
        facility.status === "paused" ? "opacity-75" : ""
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{facility.name}</span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}
            >
              {status.label}
            </span>
            <EligibilityBadge facility={facility} compact />
          </div>
          <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span>Settlement: {facility.settlementTokens.join(", ")}</span>
            <span>Collateral: {facility.collateralTokens.join(", ")}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm flex-shrink-0">
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Rate</div>
          <div className="font-semibold">{facility.interestRate}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted-foreground">LTV</div>
          <div className="font-semibold">{facility.maxLTV}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-muted-foreground">Util</div>
          <div className="font-semibold">{facility.utilization}</div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">
          {title}
        </span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && (
        <div className="mt-1 text-xs sm:text-sm text-muted-foreground">
          {subtitle}
        </div>
      )}
    </div>
  );
}
